import React, { useCallback, useEffect, useState } from "react";
import { KeyRound, Mail, Inbox, Sparkles, LogOut, Loader2, Link2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { admin, adminKey, api } from "@/lib/api";
import { Container, Kicker, useSeo } from "@/components/ui-kit";
import { cn } from "@/lib/utils";
import LeadInbox from "@/components/admin/LeadInbox";
import DripDashboard from "@/components/admin/DripDashboard";
import BillingSync from "@/components/admin/BillingSync";
import ProofEditor from "@/components/admin/ProofEditor";
import LibraryDigestEditor from "@/components/admin/LibraryDigest";

const TABS = [
  { key: "leads", label: "Lead inbox", icon: Inbox },
  { key: "drip", label: "Onboarding drip", icon: Mail },
  { key: "billing", label: "Billing sync", icon: Link2 },
  { key: "digest", label: "Library digest", icon: BookOpen },
  { key: "proof", label: "Site proof", icon: Sparkles },
];

const Gate = ({ onUnlock }) => {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.get("/drip/stats", { headers: { "X-Admin-Key": key.trim() } });
      sessionStorage.setItem("fixlr_admin_key", key.trim()); onUnlock();
    } catch { toast.error("That key didn't work"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-[70vh] grid place-items-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-mk-border bg-mk-surface p-8" data-testid="admin-gate">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-mk-lime/10 border border-mk-lime/30 text-mk-lime"><KeyRound className="h-5 w-5" /></div>
        <Kicker className="mt-5">Fixlr admin</Kicker>
        <h1 className="mt-2 font-display text-2xl font-bold text-mk-text">Enter the admin key</h1>
        <p className="mt-1 text-sm text-mk-dim">It's <code className="font-mono text-mk-lime">ADMIN_API_KEY</code> in the backend .env. Stored for this tab only.</p>
        <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="admin key" className="input-dark mt-5" autoFocus data-testid="admin-key-input" />
        <button type="submit" disabled={busy || !key} className="btn-lime mt-3 w-full" data-testid="admin-key-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}</button>
      </form>
    </div>
  );
};

export default function Admin() {
  useSeo({ title: "Admin", description: "Fixlr admin", path: "/admin", noindex: true });
  const [unlocked, setUnlocked] = useState(!!adminKey());
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "leads");
  const [newLeads, setNewLeads] = useState(null);
  const onLeadCounts = useCallback((counts) => setNewLeads(counts?.status?.new ?? 0), []);

  useEffect(() => {
    if (!unlocked) return;
    admin.leads().then((d) => onLeadCounts(d.counts)).catch(() => { sessionStorage.removeItem("fixlr_admin_key"); setUnlocked(false); });
  }, [unlocked, onLeadCounts]);

  if (!unlocked) return <Gate onUnlock={() => setUnlocked(true)} />;

  return (
    <div data-testid="admin-page">
      <div className="border-b border-mk-border bg-mk-surface/60">
        <Container className="py-6 flex flex-wrap items-center gap-4">
          <div>
            <Kicker>Fixlr admin</Kicker>
            <h1 className="mt-1 font-display text-2xl sm:text-3xl font-bold text-mk-text">Growth console</h1>
          </div>
          <nav className="ml-auto flex flex-wrap gap-1 rounded-full border border-mk-border bg-mk-bg p-1" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`} className={cn("inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors", tab === t.key ? "bg-mk-lime text-mk-bg font-semibold" : "text-mk-dim hover:text-mk-text")}>
                <t.icon className="h-4 w-4" />{t.label}
                {t.key === "leads" && newLeads > 0 && <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-mono leading-none", tab === t.key ? "bg-mk-bg text-mk-lime" : "bg-mk-lime text-mk-bg")} data-testid="admin-leads-badge">{newLeads}</span>}
              </button>
            ))}
          </nav>
          <button type="button" onClick={() => { sessionStorage.removeItem("fixlr_admin_key"); setUnlocked(false); }} className="btn-ghost !py-2 !px-3 text-xs" data-testid="admin-lock-btn"><LogOut className="h-3.5 w-3.5" />Lock</button>
        </Container>
      </div>
      <Container className="py-10">
        {tab === "leads" && <LeadInbox onCounts={onLeadCounts} />}
        {tab === "drip" && <DripDashboard />}
        {tab === "billing" && <BillingSync />}
        {tab === "digest" && <LibraryDigestEditor />}
        {tab === "proof" && <ProofEditor />}
      </Container>
    </div>
  );
}
